import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object } from "yup";
import { useParams } from "react-router-dom";
import useAxios from "@hooks/use-axios";
import Validations from "@src/utility/helpers/validations";

const DEFAULT_VALUES = {
    kpiAttributes: []
};

const useKpi = () => {
    const { platformId } = useParams();
    const { axiosInstance, loading, cancelAllRequests } = useAxios({
        base         : "panel",
        initialLoader: true
    });
    const { arrayMinMaxLengthValidation } = Validations();

    const [data, setData] = useState({ allAttributes: [], selectedKpis: [] });
    const [modalConfig, setModalConfig] = useState({ isOpen: false });

    const validationSchema = object().shape({
        kpiAttributes: arrayMinMaxLengthValidation({
            name  : "attribute",
            min   : 1,
            msgMin: "Please select at least one attribute",
            msgMax: "You cannot select more than allowed"
        })
    });

    const methods = useForm({
        defaultValues: DEFAULT_VALUES,
        resolver     : yupResolver(validationSchema)
    });

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors }
    } = methods;

    const selectedAttributes = watch("kpiAttributes");

    const getAllAttributes = async () => {
        const { data: response } = await axiosInstance.get(
            `/kpi-setting/${platformId}/attribute`
        );
        if (response.success) {
            setData({ allAttributes: response.data });
        }
    };

    const getSelectedKpis = async () => {
        const { data } = await axiosInstance.get(`/kpi-setting/${platformId}`);
        if (data) {
            setData({ selectedKpis: data });
        }
    };

    const handleRowActions = async (action, row = null) => {
        if (action === "delete") {
            await axiosInstance.delete(`/kpi-setting/${platformId}/${row._id}`);
            getSelectedKpis();
            return;
        }

        if (action === "add") {
            handleSubmit(async (formData) => {
                if (!formData.kpiAttributes || formData.kpiAttributes.length === 0) return;

                const payload = formData.kpiAttributes.map((attr) => ({
                    name: attr.value
                }));

                const { data } = await axiosInstance.post(
                    `/kpi-setting/${platformId}/`,
                    { data: payload }
                );

                if (data) {
                    getAllAttributes();
                    getSelectedKpis();
                    reset(DEFAULT_VALUES);
                }
            })();
        }
    };

    useEffect(() => {
        let isActive = true;
        if (isActive) getSelectedKpis();
        return () => {
            isActive = false;
            cancelAllRequests();
        };
    }, [platformId]);

    return {
        data,
        loading,
        getSelectedKpis,
        handleRowActions,
        setModalConfig,
        modalConfig,
        methods,
        control,
        errors,
        selectedAttributes
    };
};

export default useKpi;
